import { useEffect, useRef } from 'react';
import { ScrollView, type LayoutChangeEvent } from 'react-native';
import {
  Chip,
  Colors,
  Flex,
  IconChevronDown,
  useScreenSize,
} from '@banhala/ui-kit';
import { useWebDragToScroll } from 'shared/src/hooks';
import type {
  FavoriteMarketsCategory,
  FavoriteMarketsOrder,
} from 'shared/src/api/favoriteMarketService/getFavoriteMarketsFilterOptions/types';

interface MarketFilterBarProps {
  selectedOrder: FavoriteMarketsOrder;
  categories: FavoriteMarketsCategory[];
  selectedCategorySno: number | null;
  onPressSort: () => void;
  onPressCategory: (sno: number | null) => void;
}

export const MarketFilterBar = ({
  selectedOrder,
  categories,
  selectedCategorySno,
  onPressSort,
  onPressCategory,
}: MarketFilterBarProps) => {
  const scrollRef = useRef<ScrollView>(null);
  useWebDragToScroll(scrollRef);
  const layoutsRef = useRef<Map<number, { x: number; width: number }>>(
    new Map(),
  );
  const { width: screenWidth } = useScreenSize();

  useEffect(() => {
    if (selectedCategorySno == null) return;
    const layout = layoutsRef.current.get(selectedCategorySno);
    if (layout == null) return;
    const targetX = Math.max(0, layout.x + layout.width / 2 - screenWidth / 2);
    scrollRef.current?.scrollTo({ x: targetX, animated: true });
  }, [selectedCategorySno, screenWidth]);

  const handleChipLayout = (sno: number) => (event: LayoutChangeEvent) => {
    const { x, width } = event.nativeEvent.layout;
    layoutsRef.current.set(sno, { x, width });
  };

  return (
    <Flex backgroundColor={'content_always_white'}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingVertical: 12,
          gap: 8,
        }}
      >
        <Chip
          size={'medium'}
          state={'selected'}
          rightIcon={<IconChevronDown fill={Colors.content_always_white} />}
          onPress={onPressSort}
          testID={`찜한마켓정렬필터?value=${selectedOrder.name}`}
        >
          {selectedOrder.name}
        </Chip>
        <Chip
          size={'medium'}
          state={selectedCategorySno == null ? 'selected' : 'enabled'}
          onPress={() => onPressCategory(null)}
          testID={`카테고리필터?value=전체`}
        >
          전체
        </Chip>
        {categories.map(category => (
          <Flex key={category.sno} onLayout={handleChipLayout(category.sno)}>
            <Chip
              size={'medium'}
              state={
                category.sno === selectedCategorySno ? 'selected' : 'enabled'
              }
              onPress={() => onPressCategory(category.sno)}
              testID={`카테고리필터?value=${category.name}`}
            >
              {category.name}
            </Chip>
          </Flex>
        ))}
      </ScrollView>
    </Flex>
  );
};
